import { type ApolloError, useQuery } from '@apollo/client';

import { getCmsLocaleString } from 'js/lib/language';
import { useLocation, useParams } from 'js/lib/useRouter';

import { useDomainList } from 'bundles/browse-common/contexts/DomainListContext';
import { EntityFAQQuery } from 'bundles/browse-common/queries/BrowseEntityFAQ';
import { BrowseFAQQuery } from 'bundles/browse-common/queries/BrowseFAQ';
import type { BrowseEntityPageContentProps, BrowseFaq } from 'bundles/browse-common/types/faqs';
import { getGrowthCmsGraphqlContext } from 'bundles/cms/utils/CmsUtils';
import { isFreeQuery } from 'bundles/search-common/utils/pageConfigUtils';

type UseBrowseEntityContentfulFaqsParams = {
  isEntityPage?: boolean;
};

// Type for the query response
type BrowseEntityPageResult = {
  browseEntityPageCollection?: {
    items: Array<{
      domainSlug?: string;
      slug?: string;
      faqsCollection?: {
        items: Array<{
          sectionName?: string;
          question?: string;
          answer?: string;
        } | null>;
      };
    } | null>;
  };
};

const SUFFIX_FOR_EXPERIMENT_FAQS = '-v2';

export const useBrowseEntityContentfulFaqs = ({
  isEntityPage = false,
}: UseBrowseEntityContentfulFaqsParams): {
  loading: boolean;
  error?: ApolloError;
  pageContent?: BrowseEntityPageContentProps;
} => {
  const params = useParams();
  const location = useLocation();
  const domainListContext = useDomainList();

  const previewParam = location.query.preview;
  const isPreview = !!previewParam && Number(previewParam) === 1;

  // Remove v2 suffix and update Contentful content after experiment
  const domainSlug = `${params?.domainSlug || 'root'}${SUFFIX_FOR_EXPERIMENT_FAQS}`;
  const domainName = domainListContext?.domains?.find((domain) => domain.slug === domainSlug)?.name;

  let contentfulPageId: string;

  if (isEntityPage) {
    const searchQuery = location.query.query;
    contentfulPageId = isFreeQuery(searchQuery) ? 'free' : searchQuery;
  } else {
    contentfulPageId = domainSlug;
  }

  const queryVariables = {
    locale: getCmsLocaleString(),
    pageTypeSlug: isEntityPage ? 'entity' : 'browse',
    preview: !!isPreview,
    domainSlug,
    contentfulPageId,
  };

  const query = isEntityPage ? EntityFAQQuery : BrowseFAQQuery;

  const { data, loading, error } = useQuery<BrowseEntityPageResult>(query, {
    variables: queryVariables,
    context: getGrowthCmsGraphqlContext(isPreview),
    skip: !contentfulPageId,
  });

  const resp = data?.browseEntityPageCollection?.items[0];

  const pageContent: BrowseEntityPageContentProps =
    resp && resp.slug
      ? {
          slug: resp.slug,
          domainSlug: resp.domainSlug,
          name: domainName,
          faqs:
            resp.faqsCollection?.items
              .map((item) => {
                if (!item || !item.question || !item.answer) return null;
                const faq: BrowseFaq = {
                  question: item.question,
                  answer: item.answer,
                };
                return faq;
              })
              .filter(Boolean) || [],
        }
      : {};

  return { loading, error, pageContent };
};

export default useBrowseEntityContentfulFaqs;
